import React, { useState, useEffect, useRef } from 'react';
import { Wine, ChevronDown, ChevronUp, Grape, Thermometer, Sparkles } from 'lucide-react';
import type { AIContext } from '../utils/types';
import { ga4Event } from '../utils/ga4';

interface AIContextBannerProps {
  context: AIContext;
  isThinking?: boolean;
}

const AIContextBanner: React.FC<AIContextBannerProps> = ({ context, isThinking }) => {
  const [expanded, setExpanded] = useState(false);
  const prevSourceRef = useRef(context.source);

  // Auto-expand when AI result arrives (source transitions to 'ai')
  useEffect(() => {
    if (prevSourceRef.current !== 'ai' && context.source === 'ai') {
      setExpanded(true);
    }
    prevSourceRef.current = context.source;
  }, [context.source]);

  // GA4: report a shown sommelier context once per intent type + source
  // (pending stubs excluded — they're a loading state, not an answer).
  const ga4SentRef = useRef('');
  useEffect(() => {
    if (context.source === 'pending') return;
    const key = `${context.type}|${context.source ?? 'rules'}`;
    if (ga4SentRef.current === key) return;
    ga4SentRef.current = key;
    ga4Event('ai_context_shown', { intent_type: context.type, ai_source: context.source ?? 'rules' });
  }, [context.type, context.source]);

  const hasWineRecs = context.wineRecommendations && context.wineRecommendations.length > 0;
  const hasDetails =
    context.reasoning ||
    (context.wineCharacteristics && context.wineCharacteristics.length > 0) ||
    hasWineRecs ||
    (context.suggestedGrapes && context.suggestedGrapes.length > 0) ||
    context.servingTemp;

  return (
    <div className="mb-4 px-4 py-3 bg-gradient-to-r from-[#4A0E2E]/5 to-[#722F37]/5 border border-[#722F37]/15 rounded-xl animate-fade-in">
      <div className="flex items-center gap-3">
        <Wine className="h-5 w-5 text-[#722F37] flex-shrink-0" />
        <p className="text-sm font-medium text-[#4A0E2E] flex-1">{context.message}</p>
        {hasDetails && (
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-[#722F37]/70 hover:text-[#722F37] transition-colors flex-shrink-0"
          >
            <span>{expanded ? 'Λιγότερα' : 'Περισσότερα'}</span>
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
          </button>
        )}
      </div>

      {isThinking && (
        <div className="mt-2 flex items-center gap-2 animate-fade-in">
          <div className="relative h-3.5 w-3.5 flex-shrink-0">
            <div className="absolute inset-0 rounded-full bg-[#C5A572]/30 animate-ping" />
            <div className="absolute inset-[3px] rounded-full bg-[#C5A572]/70" />
          </div>
          <span className="text-[11px] text-[#722F37]/60 italic">
            Ο AI Sommelier σκέφτεται...
          </span>
        </div>
      )}

      {expanded && hasDetails && (
        <div className="mt-3 pt-3 border-t border-[#722F37]/10 space-y-2.5 animate-fade-in">
          {context.reasoning && (
            <div className="flex items-start gap-2">
              <Sparkles className="h-4 w-4 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <p className="text-xs text-[#4A0E2E]/80 leading-relaxed">{context.reasoning}</p>
            </div>
          )}

          {context.wineCharacteristics && context.wineCharacteristics.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {context.wineCharacteristics.map((char) => (
                <span
                  key={char}
                  className="inline-flex items-center px-2 py-0.5 text-[10px] font-medium text-[#722F37] bg-[#722F37]/8 rounded-full"
                >
                  {char}
                </span>
              ))}
            </div>
          )}

          {hasWineRecs ? (
            <div className="space-y-1.5">
              {context.wineRecommendations!.map((rec) => (
                <div key={rec.grape} className="flex items-start gap-2">
                  <Grape className="h-3.5 w-3.5 text-[#722F37]/60 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="text-[11px] font-medium text-[#4A0E2E]">{rec.grape}</span>
                    {rec.category && (
                      <span className="ml-1.5 text-[9px] text-[#722F37]/50">({rec.category})</span>
                    )}
                    <p className="text-[10px] text-[#4A0E2E]/60 leading-relaxed">{rec.description}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : context.suggestedGrapes && context.suggestedGrapes.length > 0 ? (
            <div className="flex items-center gap-2">
              <Grape className="h-3.5 w-3.5 text-[#722F37]/60 flex-shrink-0" />
              <span className="text-[10px] text-[#4A0E2E]/60">
                {context.suggestedGrapes.join(' · ')}
              </span>
            </div>
          ) : null}

          {context.servingTemp && (
            <div className="flex items-center gap-2">
              <Thermometer className="h-3.5 w-3.5 text-[#722F37]/60 flex-shrink-0" />
              <span className="text-[10px] text-[#4A0E2E]/60">{context.servingTemp}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(AIContextBanner);
